import type React from 'react';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChartColumn, ChevronRight, Lock } from 'lucide-react';
import { PinAccessDrawer } from '@components/community/PinAccessDrawer';
import { useOverlayGrant } from '@hooks/useOverlayGrant';
import { useIsBehindPostSheet } from '@components/PostSheet/postSheetBackgroundContext';
import { ackIntent, getIntents, useIntents } from '@lib/overlay/intents';
import { postSheetPlaceOf } from '@lib/postSheet/postSheetLocation';
import { getCommunityPath } from '@utils/slugHelpers';
import type { EventSummary, SpaceSummary } from '@models/api/question';
import { LiveBadge } from '@components/live';
import { LivePollFeedCard } from '@components/livePolls/LivePollFeedCard';
import { cn } from '@lib/utils';
import { rereadPollQuestion } from '@lib/cache/polls';
import { usePollVote, type PollCannotJoin, type PollPinRequest } from '@hooks/usePollVote';
import type { PollRead } from '@models/api/poll';
import { POLL_COPY, pollTopLine } from './pollCopy';
import { PollSavedLine, PollSignInBlock, usePollSignedOutAnswer } from './PollSignedOut';
import { pollDisplay } from './pollDisplay';
import { formatPollTime } from './pollFormat';
import { PollFooter } from './PollFooter';
import { PollQuestionBlock } from './PollQuestionBlock';
import { usePollClock } from './usePollClock';

export interface PollCardBodyProps {
  question: {
    id: string;
    slug: string | null;
    title: string;
    content: string;
    is_my_question: boolean;
    /** The post's Spaces as the viewer sees them (home first): names for joins, B7's avatar. */
    space_summaries?: SpaceSummary[] | null;
    event_summaries?: EventSummary[] | null;
  };
  poll: PollRead;
  /**
   * Opens the poll (the Post sheet). The card body is not a button for a poll (its options and
   * meters must stay readable), so the question is the keyboard's way in; a tap anywhere on the card
   * still opens it through the card's own click. Left out inside the sheet, where nothing opens.
   */
  onOpen?: () => void;
  /** Spread on the opener so the sheet returns focus to it on close. */
  openerProps?: Record<string, string>;
  /** The question's heading: 2 in a feed or the sheet, 1 on the old post page. */
  headingLevel?: 1 | 2;
  /**
   * The viewer may answer here (FE-1b: signed in). Signed out, the card stays read-only until
   * FE-1d's sign-in intent; a closed poll never takes answers.
   */
  canAnswer?: boolean;
  /**
   * Signed out (FE-1d): the options tick and the answer is saved as a sign-in intent (B7, B7b,
   * E5); nothing is sent.
   */
  signedOut?: boolean;
  /** The Space being viewed (a Space feed's): the one a non-member's answer joins. */
  viewedSpaceId?: string | null;
  /** The host draws E5's line under the card itself (QuestionCard, `PollSavedLineUnderCard`). */
  savedLineOutside?: boolean;
  /** Opens "Who answered" (B6) from the footer's "See who" (FE-1c). */
  onSeeWho?: () => void;
}

/**
 * A poll in a post card (TECH-197 § Design snapshot "The card"): the top line with `chart-column`
 * (no "Poll" word), the question block, the footer, and "Some people picked more than one" under it.
 * Answering (FE-1b) through `usePollVote` when `canAnswer`.
 */
export const PollCardBody: React.FC<PollCardBodyProps> = props => {
  if (props.poll.mode !== 'live') return <StandardPollBody {...props} />;
  // TECH-867: started, anyone but the author gets the live card (L6–L8, L15), following the open question. Ended,
  // the author gets it too: the same final results (founder, 3 Nov).
  const started = props.poll.live_state === 'live' || props.poll.live_state === 'ended';
  const isAuthor = props.question.is_my_question;
  if (!started || (isAuthor && props.poll.live_state !== 'ended'))
    return <LivePollPlaceholder {...props} />;
  return (
    <LivePollFeedCard
      questionId={props.question.id}
      // Never the author, even if Back on another device reopens the poll under a stale `ended` block.
      canAnswer={props.canAnswer === true && !isAuthor}
      placeholder={<LivePollPlaceholder {...props} />}
      onOpen={props.onOpen}
      openerProps={props.openerProps}
    />
  );
};

/** The question, as the opener button when the card can open the poll. */
const PollTitle: React.FC<Pick<PollCardBodyProps, 'question' | 'onOpen' | 'openerProps'>> = ({
  question,
  onOpen,
  openerProps,
}) =>
  onOpen ? (
    <button
      type="button"
      className="rounded-sm text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mb-teal-ink"
      onClick={event => {
        // The card's own click would open it a second time.
        event.stopPropagation();
        onOpen();
      }}
      {...openerProps}
    >
      {question.title}
    </button>
  ) : (
    <>{question.title}</>
  );

/**
 * A live poll (TECH-867) where no live card is drawn yet: the question and, while it runs, the Live pill;
 * no options, results or footer, so it never reads as a standard poll that can be answered here. Shown to the
 * author before the end (FE-L1b's L9 replaces it), before Start, and on any card until its first live read lands.
 */
const LivePollPlaceholder: React.FC<PollCardBodyProps> = ({
  question,
  poll,
  onOpen,
  openerProps,
  headingLevel = 2,
}) => {
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  return (
    <div className="flex flex-col items-start gap-2" data-testid="poll-card-body" data-mode="live">
      {/* Live means running now (LiveBadge's L9 rule): not before Start, nor after the end. */}
      {poll.live_state === 'live' && <LiveBadge />}
      <Heading dir="auto" className="text-base leading-5 font-semibold text-black wrap-break-word">
        <PollTitle question={question} onOpen={onOpen} openerProps={openerProps} />
      </Heading>
    </div>
  );
};

const StandardPollBody: React.FC<PollCardBodyProps> = ({
  question,
  poll,
  onOpen,
  openerProps,
  headingLevel,
  canAnswer = false,
  signedOut = false,
  viewedSpaceId = null,
  savedLineOutside = false,
  onSeeWho,
}) => {
  const queryClient = useQueryClient();
  const rootRef = useRef<HTMLDivElement>(null);
  const { id, slug } = question;
  const vote = usePollVote({
    questionId: id,
    slug,
    isAuthor: question.is_my_question,
    spaceId: viewedSpaceId,
    spaces: question.space_summaries,
  });
  const { answer } = vote;
  // Polls off (`read_only`): no tap for anyone, so a guest holds no answer and nothing says it will count.
  const readOnly = poll.read_only === true;
  const guestEnabled = signedOut && !poll.is_closed && !readOnly;
  const guest = usePollSignedOutAnswer({
    questionId: id,
    poll,
    place: placeOf(question),
    enabled: guestEnabled,
  });
  usePollPinReplay(id, canAnswer && !readOnly, poll.is_closed, vote.requestPin);

  const now = usePollClock({
    targetRef: rootRef,
    closesAt: poll.closes_at,
    enabled: !poll.is_closed && poll.closes_at !== null,
    onEnd: () => {
      void rereadPollQuestion(queryClient, { id, slug, closesAt: poll.closes_at });
    },
  });

  // The end, said once (TECH-197 § Edge cases "Accessibility": the countdown is never announced;
  // a polite live region speaks when it ends). Only for an end seen on screen: a card that mounts
  // ended says nothing. Kept from the previous render, the pattern React documents.
  // Each message gets its own id: the same words twice ("Answer removed" on a second take back)
  // draw a new node, so the region speaks again instead of React keeping the text as it was.
  const [wasOpen, setWasOpen] = useState(!poll.is_closed);
  const [announcement, setAnnouncement] = useState({ text: '', id: 0 });
  const announce = (text: string) => {
    setAnnouncement(previous => ({ text, id: previous.id + 1 }));
  };
  if (wasOpen && poll.is_closed) {
    setWasOpen(false);
    announce(POLL_COPY.finalResults);
  } else if (!wasOpen && !poll.is_closed) {
    setWasOpen(true);
    announce('');
  }

  // E17: nothing to tap until the poll is read again (a new card, or a join elsewhere).
  const cannotJoin = poll.is_closed ? null : vote.cannotJoin;
  let onToggleOption: ((optionId: string) => void) | undefined;
  if (cannotJoin || readOnly) onToggleOption = undefined;
  else if (guestEnabled) onToggleOption = guest.onToggle;
  else if (canAnswer) {
    onToggleOption = (optionId: string) => {
      // Taking the answer back is said; a pick is said by the control's own checked state.
      if (answer(poll, optionId) === 'removed') announce(POLL_COPY.answerRemoved);
    };
  }

  // The picks drawn: the guest's saved ones, or the one the PIN drawer holds (E16), over the block.
  const heldPick = guestEnabled && guest.picks.length > 0 ? guest.picks : vote.pin?.optionIds;
  const shown = heldPick ? { ...poll, my_option_ids: heldPick } : poll;
  const display = pollDisplay(shown, question.is_my_question);
  const right =
    cannotJoin || (guestEnabled && guest.picks.length > 0)
      ? ({ kind: 'none' } as const)
      : display.right;
  const time = formatPollTime(poll, now);
  const topLineText = pollTopLine({
    allowsMultiple: poll.allows_multiple,
    hideVoterNames: poll.hide_voter_names,
    isClosed: poll.is_closed,
  });

  const topLine = (
    <p data-testid="poll-top-line" className="flex items-center gap-1.5 text-xs leading-4">
      <ChartColumn
        aria-hidden="true"
        className="size-3.5 shrink-0 text-mb-teal-ink"
        strokeWidth={2.25}
      />
      <span
        className={cn(
          poll.is_closed ? 'font-semibold text-mb-gray-dark' : 'font-normal text-mb-grey-300',
        )}
      >
        {topLineText}
      </span>
    </p>
  );

  return (
    <div ref={rootRef} className="flex flex-col gap-3" data-testid="poll-card-body">
      <PollQuestionBlock
        mode="card"
        onFailure="revert"
        poll={shown}
        rows={display.rows}
        dimmed={cannotJoin !== null}
        topLine={topLine}
        question={<PollTitle question={question} onOpen={onOpen} openerProps={openerProps} />}
        headingLevel={headingLevel}
        context={question.content}
        onToggleOption={onToggleOption}
      />
      {cannotJoin && <PollCannotJoinBlock cannotJoin={cannotJoin} />}
      {guestEnabled && guest.showSignInBlock && <PollSignInBlock onSignIn={guest.openSignIn} />}
      <PollFooter time={time} right={right} onSeeWho={onSeeWho} />
      {display.somePickedMoreThanOne && (
        <p className="text-xs leading-4 font-normal text-mb-dark-gray">
          {POLL_COPY.somePickedMoreThanOne}
        </p>
      )}
      {guestEnabled && guest.showSavedLine && !savedLineOutside && (
        <PollSavedLine onSignIn={guest.openSignIn} className="px-0" />
      )}
      <div aria-live="polite" className="sr-only" data-testid="poll-live-region">
        {announcement.text && <span key={announcement.id}>{announcement.text}</span>}
      </div>
      <PollPinDrawer questionId={id} poll={poll} vote={vote} />
    </div>
  );
};

/** B7's place: the poll's home Space, else its Event. */
const placeOf = (question: PollCardBodyProps['question']) => {
  const place = postSheetPlaceOf(question);
  return place === 'none' ? null : { name: place.name, imageUrl: place.imageUrl };
};

/**
 * An answer cast after sign-in that came back `POLL_PIN_REQUIRED` (`executePostAuthIntent` posts a
 * `poll-pin` intent): the poll's card opens E16 still holding the pick. Only the card in front (the
 * Post sheet's, or the post page's), never one behind the sheet; the first to ack it wins. On an
 * ended poll it is dropped unopened.
 */
const usePollPinReplay = (
  questionId: string,
  canAnswer: boolean,
  closed: boolean,
  requestPin: (request: { optionIds: string[]; spaceId: string | null }) => void,
) => {
  const behindSheet = useIsBehindPostSheet();
  const intentId = useIntents('poll-pin').find(
    intent => intent.payload.questionId === questionId,
  )?.id;
  const claim = useEffectEvent((id: string) => {
    const intent = getIntents().find(
      (candidate): candidate is Extract<typeof candidate, { surface: 'poll-pin' }> =>
        candidate.id === id && candidate.surface === 'poll-pin',
    );
    if (!intent) return;
    ackIntent(id);
    // A poll that ended since the answer was replayed has nothing left to join for: the intent
    // goes without opening E16 (FE-1d review).
    if (closed) return;
    requestPin({ optionIds: intent.payload.optionIds, spaceId: intent.payload.spaceId });
  });
  // The intent channel is the external store; acting on it once the card is on screen.
  useEffect(() => {
    if (intentId && canAnswer && !behindSheet) claim(intentId);
  }, [intentId, canAnswer, behindSheet]);
};

/**
 * E16, E16b: today's PIN drawer with the poll line ("You picked {option}. Enter the PIN to join,
 * and your answer goes in."). Join sends the pick with the PIN, "Request access" as a request (the
 * answer held, B11); a wrong PIN shows today's line; Cancel casts nothing.
 */
const PollPinDrawer: React.FC<{
  questionId: string;
  poll: PollRead;
  vote: ReturnType<typeof usePollVote>;
}> = ({ questionId, poll, vote }) => {
  // The last request stays drawn while the drawer slides away.
  const [request, setRequest] = useState<PollPinRequest | null>(vote.pin);
  if (vote.pin && vote.pin !== request) setRequest(vote.pin);
  const texts = request
    ? poll.options.filter(option => request.optionIds.includes(option.id)).map(o => o.text)
    : [];
  const wanted = vote.pin !== null;
  // Android Back closes E16 (the overlay mutex), never the Post sheet under it; while a Join or
  // Request access is in flight it does nothing, as the drawer's own close (FE-1d review).
  const grant = useOverlayGrant(`poll-pin:${questionId}`, {
    onBackButton: () => {
      vote.cancelPin();
    },
  });
  // The vote hook's PIN state is the source; the grant follows it.
  useEffect(() => {
    if (wanted) grant.show();
    else grant.dismiss('programmatic');
  }, [wanted, grant]);
  return (
    <PinAccessDrawer
      open={wanted && grant.open}
      onOpenChange={open => {
        if (!open) vote.cancelPin();
      }}
      onSubmitPin={code => {
        vote.submitPin(poll, code);
      }}
      onRequestAccess={() => {
        vote.requestAccess(poll);
      }}
      isSubmitting={vote.isSending}
      error={vote.pin?.error ?? null}
      description={POLL_COPY.pinLine(texts)}
    />
  );
};

/**
 * E17 (P13): a lock and "Only members of {Space} can answer", the link "Open {Space}" to its page;
 * when the server named no Space (one the viewer may not be told), the line without a name or link.
 */
const PollCannotJoinBlock: React.FC<{ cannotJoin: PollCannotJoin }> = ({ cannotJoin }) => {
  const { space } = cannotJoin;
  return (
    <div className="flex flex-col" data-testid="poll-cannot-join">
      <p className="flex items-center gap-2 text-sm leading-5 text-mb-gray-dark">
        <Lock aria-hidden="true" className="size-4 shrink-0 text-mb-dark-gray" strokeWidth={2} />
        {space ? POLL_COPY.onlyMembersCanAnswer(space.name) : POLL_COPY.onlyMembersCanAnswerUnnamed}
      </p>
      {space?.slug && (
        <Link
          to={getCommunityPath({ id: null, slug: space.slug })}
          className="flex min-h-11 items-center gap-0.5 self-start pl-6 text-sm leading-5 font-medium text-mb-teal-ink"
          onClick={event => {
            // The card's own click would open the Post sheet.
            event.stopPropagation();
          }}
        >
          {POLL_COPY.openSpace(space.name)}
          <ChevronRight aria-hidden="true" className="size-4" strokeWidth={2} />
        </Link>
      )}
    </div>
  );
};

/**
 * E5 under a feed card or the sheet's card (the frame draws it outside the card, 12px below):
 * shown by hosts that pass `savedLineOutside` to `PollCardBody`.
 */
export const PollSavedLineUnderCard: React.FC<{
  question: PollCardBodyProps['question'];
  poll: PollRead;
}> = ({ question, poll }) => {
  const guest = usePollSignedOutAnswer({
    questionId: question.id,
    poll,
    place: placeOf(question),
    enabled: poll.mode === 'standard' && !poll.is_closed && poll.read_only !== true,
  });
  if (!guest.showSavedLine) return null;
  return <PollSavedLine onSignIn={guest.openSignIn} className="mt-3" />;
};
